import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Card, DataTable, Drawer, Tabs, FormField, TextInput, TextArea, StatusBadge, Select, Modal, ConfirmDialog } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { PhoneCell } from '@/components/telephony/PhoneCell';
import { adminApi } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';
import { fmtDuration } from '@/shared/hooks/useTicker';
import { downloadCsv } from '@/shared/lib/downloadCsv';
import './leads/leads.css';
import { ScreenToolbar, FilterRow, SearchField, MutedCell, timeAgo } from './leads/Toolbar';

interface Contact {
  id: number; name?: string; phone: string; email?: string; company?: string | null;
  status?: string; owner_id?: number | null; owner_name?: string | null;
  tags?: string[]; last_call_at?: string; call_count?: number;
  external_source?: string | null; external_id?: string | null;
}

/** תג קטן "מ-CRM" לאיש קשר שסונכרן מ-Kliento. */
function CrmBadge({ source, id }: { source?: string | null; id?: string | null }) {
  if (source !== 'crm') return null;
  return <span title={id ? `CRM: ${id}` : undefined}><StatusBadge tone="info" size="sm">מ-CRM</StatusBadge></span>;
}
interface ContactNote { id: number; body: string; user_name?: string | null; created_at: string }
interface IvrChoice { digit?: string; label?: string; action?: string; target?: string; at?: string }
interface CallIvr { menu?: string; choices?: IvrChoice[] }
interface ContactCall { id: number; direction: string; status?: string; started_at: string; talk_sec?: number; disposition?: string | null; recording_url?: string | null; ivr?: CallIvr | null }
interface TimelineItem { type: 'call' | 'note'; at: string; label: string; detail: string }
interface LeadLink { id: number; campaign?: string; status: string; disposition?: string | null }
interface ContactDetail extends Contact {
  fields?: Record<string, unknown>;
  notes?: ContactNote[];
  calls?: ContactCall[];
  timeline?: TimelineItem[];
  leads?: LeadLink[];
}
interface AgentOpt { id: number; name: string }
interface CrmStats { total: number; new_this_month: number; avg_calls: number; by_status: Record<string, number> }

const STATUS_OPTS = [
  { value: 'active', label: 'פעיל' },
  { value: 'lead', label: 'ליד' },
  { value: 'customer', label: 'לקוח' },
  { value: 'archived', label: 'בארכיון' },
];
const STATUS_HE: Record<string, string> = Object.fromEntries(STATUS_OPTS.map((o) => [o.value, o.label]));
const statusTone = (s?: string) => (s === 'customer' ? 'ok' : s === 'lead' ? 'info' : s === 'archived' ? 'neutral' : 'busy');

/**
 * switcher — בורר "לידים / אנשי קשר" של המסך המאוחד, מצויר בשורת הפעולות.
 * onTotal — מדווח את סה"כ אנשי הקשר לספירה שבבורר.
 */
export function Crm({ switcher, onTotal }: { switcher?: ReactNode; onTotal?: (n: number) => void } = {}) {
  const [rows, setRows] = useState<Contact[]>([]);
  const [stats, setStats] = useState<CrmStats | null>(null);
  const [agents, setAgents] = useState<AgentOpt[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<ContactDetail | null>(null);
  const [tab, setTab] = useState<'info' | 'calls' | 'notes' | 'timeline'>('info');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmState, setConfirmState] = useState<
    | { kind: 'bulkDelete'; count: number }
    | { kind: 'deleteContact'; id: number }
    | null
  >(null);

  const filters = useMemo(() => ({ q: q || undefined, status: status || undefined, owner_id: ownerId ? Number(ownerId) : undefined, tag: tag || undefined }), [q, status, ownerId, tag]);

  const load = useCallback(() => {
    setLoading(true);
    setSelected(new Set());
    void adminApi.crm({ ...filters, per_page: 200 })
      .then((res) => setRows(res.data as Contact[]))
      .catch(() => toast('שגיאה בטעינת CRM', 'danger'))
      .finally(() => setLoading(false));
    void adminApi.contactStats().then((s) => {
      setStats(s as CrmStats);
      if (typeof (s as CrmStats)?.total === 'number') onTotal?.((s as CrmStats).total);
    }).catch(() => setStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    void adminApi.agents({ per_page: 500 }).then((res) => setAgents((res.data as AgentOpt[]) ?? []));
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  async function open(r: Contact) {
    setTab('info');
    try { setDrawer((await adminApi.crmContact(r.id)) as ContactDetail); }
    catch { toast('שגיאה בטעינת איש קשר', 'danger'); }
  }
  async function reloadDrawer() {
    if (!drawer) return;
    setDrawer((await adminApi.crmContact(drawer.id)) as ContactDetail);
  }

  function toggle(id: number) { setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); }

  function bulk(action: 'assign' | 'status' | 'tag' | 'delete', extra?: { owner_id?: number | null; status?: string; tag?: string }) {
    if (!selected.size) return;
    if (action === 'delete') { setConfirmState({ kind: 'bulkDelete', count: selected.size }); return; }
    void runBulk(action, extra);
  }

  async function runBulk(action: 'assign' | 'status' | 'tag' | 'delete', extra?: { owner_id?: number | null; status?: string; tag?: string }) {
    try {
      const n = await adminApi.bulkContacts(action, [...selected], extra);
      toast(`עודכנו ${n} אנשי קשר`, 'ok');
      setSelected(new Set());
      load();
    } catch { toast('שגיאה בפעולה מרוכזת', 'danger'); }
  }

  function removeContact() {
    if (!drawer) return;
    setConfirmState({ kind: 'deleteContact', id: drawer.id });
  }

  async function runDeleteContact(id: number) {
    try { await adminApi.deleteContact(id); toast('נמחק', 'ok'); setDrawer(null); load(); }
    catch { toast('שגיאה במחיקה', 'danger'); }
  }

  const cols: DataTableColumn<Contact>[] = useMemo(() => [
    { key: '_sel', he: '', render: (r) => <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} onClick={(e) => e.stopPropagation()} aria-label="בחר" /> },
    { key: 'phone', he: 'טלפון', render: (r) => <PhoneCell number={r.phone} size="sm" /> },
    { key: 'name', he: 'שם', render: (r) => <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>{r.name || '—'}<CrmBadge source={r.external_source} id={r.external_id} /></span> },
    { key: 'company', he: 'חברה', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.company || '—'}</span> },
    { key: 'status', he: 'סטטוס', render: (r) => <StatusBadge tone={statusTone(r.status)} size="sm">{STATUS_HE[r.status ?? ''] ?? r.status ?? '—'}</StatusBadge> },
    { key: 'owner_name', he: 'אחראי', render: (r) => <span style={{ color: 'var(--text-2)' }}>{r.owner_name || '—'}</span> },
    { key: 'call_count', he: 'שיחות', render: (r) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.call_count ?? 0}</span> },
    { key: 'last_call_at', he: 'מגע אחרון', render: (r) => <MutedCell>{timeAgo(r.last_call_at)}</MutedCell> },
  ], [selected]);

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <ScreenToolbar
        switcher={switcher}
        filters={<>
          <Select value={ownerId} onChange={setOwnerId} options={[{ value: '', label: 'כל האחראים' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
          <TextInput value={tag} onChange={(e) => setTag(e.target.value)} placeholder="תגית" style={{ width: 140 }} />
        </>}
        actions={<>
          <button
            type="button"
            className="btn"
            onClick={() =>
              downloadCsv('/admin/crm/export', filters as Record<string, unknown>, 'merkaziya-crm.csv')
                .catch(() => toast('שגיאה בייצוא', 'danger'))
            }
          >
            <Icon name="download" size={14} /> ייצוא
          </button>
          <button type="button" className="btn" onClick={() => setImportOpen(true)}><Icon name="upload" size={14} /> ייבוא</button>
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}><Icon name="plus" size={14} /> איש קשר חדש</button>
        </>}
      />

      <FilterRow>
        <SearchField value={q} onChange={setQ} placeholder="חיפוש שם/טלפון/אימייל/חברה" />
        {/* בורר הסטטוסים כמקטעים; הספירות מ-contactStats. */}
        <Tabs
          items={[{ value: '', label: 'הכל' }, ...STATUS_OPTS].map((o) => ({
            id: o.value || 'all',
            label: o.label,
            count: stats ? (o.value ? (stats.by_status?.[o.value] ?? 0) : stats.total) : undefined,
          }))}
          value={status || 'all'}
          onChange={(id) => setStatus(id === 'all' ? '' : id)}
        />
      </FilterRow>

      {selected.size > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)' }}>
          <span style={{ fontSize: 'var(--t-sm)' }}>{selected.size} נבחרו</span>
          <Select value="" onChange={(v) => v && bulk('assign', { owner_id: Number(v) })} options={[{ value: '', label: 'שייך לאחראי…' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} />
          <Select value="" onChange={(v) => v && bulk('status', { status: v })} options={[{ value: '', label: 'שנה סטטוס…' }, ...STATUS_OPTS]} />
          <button type="button" className="btn danger" onClick={() => bulk('delete')}>מחק</button>
          <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>נקה</button>
        </div>
      )}

      <Card padded={false} style={{ '--d': '.05s' } as React.CSSProperties}><DataTable<Contact> columns={cols} rows={rows} loading={loading} onRowClick={open} emptyMessage="אין אנשי קשר." /></Card>

      <Drawer isOpen={!!drawer} onClose={() => setDrawer(null)} title={drawer?.name || drawer?.phone}>
        {drawer && (
          <>
            <Tabs items={[
              { id: 'info', label: 'פרטים' },
              { id: 'timeline', label: 'ציר זמן' },
              { id: 'calls', label: `שיחות (${drawer.calls?.length ?? 0})` },
              { id: 'notes', label: `הערות (${drawer.notes?.length ?? 0})` },
            ]} value={tab} onChange={(id) => setTab(id as never)} />
            <div style={{ padding: 'var(--pad-card)' }}>
              {tab === 'info' && <InfoTab contact={drawer} agents={agents} onSaved={reloadDrawer} onDelete={removeContact} />}
              {tab === 'timeline' && <TimelineTab items={drawer.timeline ?? []} leads={drawer.leads ?? []} />}
              {tab === 'calls' && <CallsTab calls={drawer.calls ?? []} />}
              {tab === 'notes' && <NotesTab contactId={drawer.id} notes={drawer.notes ?? []} onChanged={reloadDrawer} />}
            </div>
          </>
        )}
      </Drawer>

      {createOpen && <ContactModal agents={agents} onClose={() => setCreateOpen(false)} onSaved={() => { setCreateOpen(false); load(); }} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />}

      <ConfirmDialog
        isOpen={!!confirmState}
        onClose={() => setConfirmState(null)}
        onConfirm={() => {
          if (!confirmState) return;
          if (confirmState.kind === 'bulkDelete') void runBulk('delete');
          else void runDeleteContact(confirmState.id);
        }}
        title={confirmState?.kind === 'bulkDelete' ? 'מחיקת אנשי קשר' : 'מחיקת איש קשר'}
        message={confirmState?.kind === 'bulkDelete' ? `למחוק ${confirmState.count} אנשי קשר?` : 'למחוק את איש הקשר וכל ההערות?'}
        confirmLabel="מחק"
        tone="danger"
      />
    </div>
  );
}

function InfoTab({ contact, agents, onSaved, onDelete }: { contact: ContactDetail; agents: AgentOpt[]; onSaved: () => void; onDelete: () => void }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    name: contact.name ?? '', email: contact.email ?? '', company: contact.company ?? '',
    status: contact.status ?? 'active', owner_id: contact.owner_id ? String(contact.owner_id) : '',
    tags: (contact.tags ?? []).join(', '),
  });
  const [fields, setFields] = useState<[string, string][]>(Object.entries(contact.fields ?? {}).map(([k, v]) => [k, String(v)]));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateContact(contact.id, {
        name: f.name || null, email: f.email || null, company: f.company || null,
        status: f.status, owner_id: f.owner_id ? Number(f.owner_id) : null,
        tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
        fields: Object.fromEntries(fields.filter(([k]) => k.trim())),
      });
      toast('נשמר', 'ok');
      setEditing(false);
      onSaved();
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה', 'danger');
    } finally { setSaving(false); }
  }

  if (editing) {
    return (
      <div className="stack">
        <FormField label="שם"><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
        <FormField label="אימייל"><TextInput type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" /></FormField>
        <FormField label="חברה"><TextInput value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></FormField>
        <FormField label="סטטוס"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS_OPTS} /></FormField>
        <FormField label="אחראי"><Select value={f.owner_id} onChange={(v) => setF({ ...f, owner_id: v })} options={[{ value: '', label: '— לא משויך' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} /></FormField>
        <FormField label="תגיות" hint="מופרדות בפסיק"><TextInput value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></FormField>
        <FormField label="שדות מותאמים">
          <div className="stack" style={{ gap: 4 }}>
            {fields.map(([k, v], i) => (
              <div key={i} style={{ display: 'flex', gap: 6 }}>
                <TextInput value={k} onChange={(e) => setFields(fields.map((p, j) => j === i ? [e.target.value, p[1]] : p))} placeholder="שם שדה" />
                <TextInput value={v} onChange={(e) => setFields(fields.map((p, j) => j === i ? [p[0], e.target.value] : p))} placeholder="ערך" />
                <button type="button" className="btn ghost" onClick={() => setFields(fields.filter((_, j) => j !== i))}>×</button>
              </div>
            ))}
            <button type="button" className="btn ghost" onClick={() => setFields([...fields, ['', '']])}>+ שדה</button>
          </div>
        </FormField>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn" onClick={() => setEditing(false)} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'שמור'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <Row k="שם" v={contact.name || '—'} />
      {contact.external_source === 'crm' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
          <span style={{ color: 'var(--muted)' }}>מקור</span><CrmBadge source={contact.external_source} id={contact.external_id} />
        </div>
      )}
      <Row k="טלפון" v={contact.phone} mono />
      <Row k="אימייל" v={contact.email || '—'} mono={!!contact.email} />
      <Row k="חברה" v={contact.company || '—'} />
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
        <span style={{ color: 'var(--muted)' }}>סטטוס</span><StatusBadge tone={statusTone(contact.status)} size="sm">{STATUS_HE[contact.status ?? ''] ?? '—'}</StatusBadge>
      </div>
      <Row k="אחראי" v={contact.owner_name || '—'} />
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
        <span style={{ color: 'var(--muted)' }}>תגיות</span>
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(contact.tags ?? []).length === 0 ? '—' : (contact.tags ?? []).map((t) => <StatusBadge key={t} tone="info" size="sm">{t}</StatusBadge>)}
        </span>
      </div>
      {Object.entries(contact.fields ?? {}).map(([k, v]) => <Row key={k} k={k} v={String(v)} />)}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <button type="button" className="btn" onClick={() => setEditing(true)}>ערוך פרטים</button>
        <button type="button" className="btn danger" onClick={onDelete}>מחק איש קשר</button>
      </div>
    </div>
  );
}

function TimelineTab({ items, leads }: { items: TimelineItem[]; leads: LeadLink[] }) {
  return (
    <div className="stack">
      {leads.length > 0 && (
        <Card title="לידים מקושרים">
          {leads.map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--t-sm)', padding: '3px 0' }}>
              <span>{l.campaign ?? 'קמפיין'}</span>
              <span style={{ color: 'var(--muted)' }}>{l.status}{l.disposition ? ` · ${l.disposition}` : ''}</span>
            </div>
          ))}
        </Card>
      )}
      {items.length === 0 ? <div style={{ color: 'var(--muted)' }}>אין פעילות.</div> : items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
          <Icon name={it.type === 'call' ? 'phone' : 'info'} size={14} color="var(--muted)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--t-sm)' }}>{it.label}</div>
            {it.detail && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{it.detail}</div>}
          </div>
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{it.at ? new Date(it.at).toLocaleString('he-IL') : ''}</span>
        </div>
      ))}
    </div>
  );
}

/** צעד במסע ה-IVR כטקסט: "הקיש 1 (תור מכירות)" / "ללא הקשה" / "נותב אוטומטית". */
export function ivrStepText(ch: IvrChoice): string {
  const base = ch.digit === 'timeout' ? 'ללא הקשה' : ch.digit ? `הקיש ${ch.digit}` : 'נותב אוטומטית';
  if (ch.label) return `${base} (${ch.label})`;
  if (ch.action === 'queue') return `${base} → תור ${ch.target ?? ''}`;
  if (ch.action === 'transfer') return `${base} → שלוחה ${ch.target ?? ''}`;
  if (ch.action === 'voicemail') return `${base} → תא קולי`;
  return base;
}

export function IvrPath({ ivr }: { ivr: CallIvr }) {
  const steps = (ivr.choices ?? []).map(ivrStepText);
  if (!ivr.menu && steps.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
      <StatusBadge tone="info" size="sm">IVR</StatusBadge>
      <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
        {[ivr.menu, ...steps].filter(Boolean).join(' → ')}
      </span>
    </div>
  );
}

function CallsTab({ calls }: { calls: ContactCall[] }) {
  if (calls.length === 0) return <div style={{ color: 'var(--muted)' }}>אין שיחות.</div>;
  return (
    <div className="stack">
      {calls.map((c) => (
        <Card key={c.id} className="ld-call-row">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{c.direction === 'inbound' ? '↙ נכנסת' : '↗ יוצאת'}{c.status ? ` · ${c.status}` : ''}{c.talk_sec ? ` · ${fmtDuration(c.talk_sec)}` : ''}{c.disposition ? ` · ${c.disposition}` : ''}</span>
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{new Date(c.started_at).toLocaleString('he-IL')}</span>
          </div>
          {c.ivr && <IvrPath ivr={c.ivr} />}
          {c.recording_url && <a href={c.recording_url} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--t-xs)', color: 'var(--accent-ink)' }}>הקלטה</a>}
        </Card>
      ))}
    </div>
  );
}

function NotesTab({ contactId, notes, onChanged }: { contactId: number; notes: ContactNote[]; onChanged: () => void }) {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);

  async function add() {
    if (!body.trim()) return;
    setSaving(true);
    try { await adminApi.addContactNote(contactId, body.trim()); toast('ההערה נוספה', 'ok'); setBody(''); onChanged(); }
    catch { toast('שגיאה בהוספת הערה', 'danger'); }
    finally { setSaving(false); }
  }
  async function del(noteId: number) {
    try { await adminApi.deleteContactNote(contactId, noteId); toast('ההערה נמחקה', 'ok'); onChanged(); }
    catch { toast('שגיאה במחיקת הערה', 'danger'); }
  }

  return (
    <div className="stack">
      <div className="stack" style={{ gap: 6 }}>
        <TextArea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="הוסף הערה…" />
        <div><button type="button" className="btn primary" onClick={add} disabled={saving || !body.trim()}>{saving ? 'מוסיף…' : 'הוסף הערה'}</button></div>
      </div>
      {notes.length === 0 ? <div style={{ color: 'var(--muted)' }}>אין הערות.</div> : notes.map((n) => (
        <Card key={n.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{new Date(n.created_at).toLocaleString('he-IL')}{n.user_name ? ` · ${n.user_name}` : ''}</div>
            <button type="button" className="btn ghost" style={{ padding: '0 6px' }} onClick={() => del(n.id)} aria-label="מחק">×</button>
          </div>
          <div>{n.body}</div>
        </Card>
      ))}
    </div>
  );
}

function ContactModal({ agents, onClose, onSaved }: { agents: AgentOpt[]; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ phone: '', name: '', email: '', company: '', status: 'active', owner_id: '', tags: '' });
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!f.phone.trim()) { toast('יש להזין טלפון', 'danger'); return; }
    setSaving(true);
    try {
      await adminApi.createContact({
        phone: f.phone.trim(), name: f.name || null, email: f.email || null, company: f.company || null,
        status: f.status, owner_id: f.owner_id ? Number(f.owner_id) : null,
        tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      toast('איש הקשר נוצר', 'ok');
      onSaved();
    } catch (e: unknown) {
      toast((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה ביצירה', 'danger');
    } finally { setSaving(false); }
  }
  return (
    <Modal isOpen onClose={onClose} title="איש קשר חדש"
      footer={<><button type="button" className="btn" onClick={onClose}>ביטול</button><button type="button" className="btn primary" onClick={save} disabled={saving}>{saving ? 'שומר…' : 'צור'}</button></>}>
      <div className="stack">
        <FormField label="טלפון" required><TextInput value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} dir="ltr" /></FormField>
        <FormField label="שם"><TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></FormField>
        <FormField label="אימייל"><TextInput type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} dir="ltr" /></FormField>
        <FormField label="חברה"><TextInput value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></FormField>
        <FormField label="סטטוס"><Select value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS_OPTS} /></FormField>
        <FormField label="אחראי"><Select value={f.owner_id} onChange={(v) => setF({ ...f, owner_id: v })} options={[{ value: '', label: '— לא משויך' }, ...agents.map((a) => ({ value: String(a.id), label: a.name }))]} /></FormField>
        <FormField label="תגיות" hint="מופרדות בפסיק"><TextInput value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} /></FormField>
      </div>
    </Modal>
  );
}

function ImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [raw, setRaw] = useState('');
  const [importing, setImporting] = useState(false);
  function parse(): { phone: string; name?: string }[] {
    return raw.split('\n').map((l) => l.trim()).filter(Boolean).flatMap((line) => {
      const [phone, ...rest] = line.split(/[,\t]/).map((s) => s.trim());
      return phone ? [{ phone, name: rest.join(' ') || undefined }] : [];
    });
  }
  async function run() {
    const contacts = parse();
    if (!contacts.length) { toast('לא נמצאו אנשי קשר', 'danger'); return; }
    setImporting(true);
    try {
      const res = await adminApi.importContacts(contacts);
      toast(`${res.imported} נוספו · ${res.updated} עודכנו${res.skipped ? ` · ${res.skipped} דולגו` : ''}`, 'ok');
      onDone();
    } catch { toast('שגיאה בייבוא', 'danger'); }
    finally { setImporting(false); }
  }
  return (
    <Modal isOpen onClose={onClose} title="ייבוא אנשי קשר"
      footer={<><button type="button" className="btn" onClick={onClose}>ביטול</button><button type="button" className="btn primary" onClick={run} disabled={importing}>{importing ? 'מייבא…' : 'ייבא'}</button></>}>
      <div className="stack">
        <FormField label="הדבק אנשי קשר" hint="שורה לכל איש קשר: טלפון,שם. כפילויות (לפי טלפון) יעודכנו.">
          <TextArea value={raw} onChange={(e) => setRaw(e.target.value)} dir="ltr" rows={10}
            placeholder={'0501234567,ישראל ישראלי\n0529876543,דנה כהן'}
            style={{ fontFamily: 'var(--font-mono)' }} />
        </FormField>
        <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{parse().length} אנשי קשר יזוהו</div>
      </div>
    </Modal>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
      <span style={{ color: 'var(--muted)' }}>{k}</span>
      <span dir={mono ? 'ltr' : undefined} style={{ fontFamily: mono ? 'var(--font-mono)' : undefined }}>{v}</span>
    </div>
  );
}
